import { motion } from "framer-motion";
import { BookOpen, Clock, Music, Shield, TrendingUp } from "lucide-react";
import React from "react";
import MegaLoader from "../../components/MegaLoader";
import { Endpoints } from "../../lib/endpoints";
import { NonPaginatedResponse } from "../../lib/responses";
import { usePerformRequest } from "../../lib/usePerformRequest";
import { IHymnProps } from "../../types";

const Overview: React.FC = () => {
  // Fetch Hymns Data for Stats
  const { data: hymnsData, isLoading } = usePerformRequest<
    NonPaginatedResponse<IHymnProps[]>
  >({
    method: Endpoints.GetHymns(1, 100).method,
    route: Endpoints.GetHymns(1, 100).route,
  });

  const hymns = hymnsData?.data || [];

  // Calculate Stats
  const totalHymns = hymns.length;
  // Assuming if copyright text exists and isn't "Public Domain", it's copyrighted
  const publicDomainCount = hymns.filter((h) =>
    h.pub?.copyright?.toLowerCase().includes("public domain"),
  ).length;
  const copyrightedCount = totalHymns - publicDomainCount;

  // Get Recent Activity (Top 5 most recently created or updated)
  const recentActivity = [...hymns]
    .sort((a, b) => (b.updatedAt || b.createdAt) - (a.updatedAt || a.createdAt))
    .slice(0, 5);

  const stats = [
    {
      label: "Total Hymns",
      value: totalHymns,
      icon: Music,
      color: "text-blue-400",
      bg: "bg-blue-400/10",
    },
    {
      label: "Public Domain",
      value: publicDomainCount,
      icon: BookOpen,
      color: "text-green-400",
      bg: "bg-green-400/10",
    },
    {
      label: "Copyrighted",
      value: copyrightedCount,
      icon: Shield,
      color: "text-purple-400",
      bg: "bg-purple-400/10",
    },
    {
      label: "System Status",
      value: "100%",
      icon: TrendingUp,
      color: "text-orange-400",
      bg: "bg-orange-400/10",
    },
  ];

  if (isLoading) return <MegaLoader />;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">
          Dashboard Overview
        </h1>
        <p className="text-gray-400">
          Welcome back! Here's what's happening with your portfolio today.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 p-6 rounded-2xl"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-gray-400 text-sm font-medium mb-1">
                  {stat.label}
                </p>
                <h3 className="text-3xl font-bold text-white">{stat.value}</h3>
              </div>
              <div className={`p-3 rounded-xl ${stat.bg}`}>
                <stat.icon className={`w-6 h-6 ${stat.color}`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Recent Activity Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Recent Hymns List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="lg:col-span-2 bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6"
        >
          <h2 className="text-xl font-bold text-white mb-6 flex items-center">
            <Clock className="w-5 h-5 mr-2 text-purple-400" />
            Recent Updates
          </h2>

          <div className="space-y-4">
            {recentActivity.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                No activity found.
              </div>
            ) : (
              recentActivity.map((hymn, i) => (
                <div
                  key={hymn.id}
                  className="flex items-center justify-between p-4 bg-black/20 rounded-xl border border-gray-800/50 hover:bg-gray-800/30 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                      <Music className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-medium">{hymn.title}</h4>
                      <p className="text-sm text-gray-400">
                        {hymn.updatedAt ? "Updated" : "Created"} on{" "}
                        {new Date(
                          hymn.updatedAt || hymn.createdAt,
                        ).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <span className="text-xs font-mono text-gray-500 bg-gray-900 px-2 py-1 rounded">
                    {hymn.pub.year}
                  </span>
                </div>
              ))
            )}
          </div>
        </motion.div>

        {/* Quick Actions Card (Optional but good for UX) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 border border-purple-500/20 rounded-2xl p-6"
        >
          <h2 className="text-xl font-bold text-white mb-4">Quick Setup</h2>
          <p className="text-gray-400 text-sm mb-6">
            Need to add a new song to your collection? Get started quickly.
          </p>
          <button
            onClick={() => (window.location.href = "/d/create-hymn")}
            className="w-full py-3 bg-white text-black font-semibold rounded-xl hover:bg-gray-200 transition-colors"
          >
            Create New Hymn
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default Overview;
